import type React from "react";
import { useEffect, useState } from "react";
import { Alert, Box, Button, Stack, TextField, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import type translations from "../../translations/translations.json";
import { updateMyProfileApi, updateUserProfileApi } from "../../api/usersApi";
import type { ProfileSectionProps } from "./profileSectionTypes";

type TranslationKeys = keyof typeof translations.en;

const ProfileNameSection: React.FC<ProfileSectionProps> = ({
  user,
  canEdit,
  isSelf,
  onUserUpdated,
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState(user.name || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    setName(user.name || "");
  }, [user.name]);

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError(t("profile.name.required" as TranslationKeys));
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const updated = isSelf
        ? await updateMyProfileApi({ name: trimmed })
        : await updateUserProfileApi(user._id, { name: trimmed });
      onUserUpdated(updated);
      setSuccess(true);
    } catch {
      setError(t("profile.name.saveFailed" as TranslationKeys));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t("profile.name.title" as TranslationKeys)}
      </Typography>
      {canEdit ? (
        <Stack spacing={2} direction={{ xs: "column", sm: "row" }} alignItems="flex-start">
          <TextField
            label={t("profile.name.label" as TranslationKeys)}
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            size="small"
          />
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={loading || name.trim() === (user.name || "").trim()}
            sx={{ minWidth: 100 }}
          >
            {t("Save" as TranslationKeys)}
          </Button>
        </Stack>
      ) : (
        <Typography variant="body1">{user.name || "—"}</Typography>
      )}
      {error ? (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      ) : null}
      {success ? (
        <Alert severity="success" sx={{ mt: 1 }}>
          {t("Success" as TranslationKeys)}
        </Alert>
      ) : null}
    </Box>
  );
};

export default ProfileNameSection;
